import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ForgotPasswordSchema, type ForgotPasswordInput } from '@app/shared';
import { apiClient } from '../lib/api-client';

export function ForgotPasswordPage(): JSX.Element {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: ForgotPasswordInput): Promise<void> => {
    setServerError(null);
    try {
      await apiClient.post('/auth/forgot-password', values);
      setSubmitted(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 429) {
        setServerError('嘗試次數過多，請稍後再試');
      } else {
        setServerError('送出失敗，請稍後再試');
      }
    }
  };

  return (
    <section className="max-w-sm mx-auto bg-white shadow-sm rounded-md p-6 mt-4">
      <h1 className="text-2xl font-semibold mb-4">忘記密碼</h1>
      {submitted ? (
        <div className="grid gap-3">
          <p role="status" className="text-sm text-slate-700">
            若此 email 在系統中存在，我們已寄送重設密碼連結。請檢查您的信箱（包含垃圾信匣）。
            連結有效時間為 1 小時。
          </p>
          <Link to="/login" className="text-blue-600 hover:underline text-sm">
            返回登入
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3">
          <p className="text-sm text-slate-600">
            請輸入您的 email，我們會寄送重設連結給您。
          </p>
          {serverError ? (
            <div role="alert" className="text-sm text-red-600">
              {serverError}
            </div>
          ) : null}
          <label className="grid gap-1">
            <span className="text-sm text-slate-700">Email</span>
            <input
              type="email"
              autoComplete="email"
              {...register('email')}
              className="border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {errors.email ? (
              <span role="alert" className="text-sm text-red-600">
                {errors.email.message}
              </span>
            ) : null}
          </label>
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-600 text-white rounded px-3 py-2 hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? '送出中…' : '送出'}
          </button>
          <Link to="/login" className="text-blue-600 hover:underline text-sm">
            返回登入
          </Link>
        </form>
      )}
    </section>
  );
}
